import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useFormContext, Controller } from "react-hook-form";
import {
  Box,
  FormLabel,
  TextField,
  FormControl,
  Typography,
  MenuItem,
  Select,
  IconButton,
  FormHelperText,
} from "@mui/material";

import { CreateServiceFormData } from "../../../../types/service.type";
import { useServiceBanners } from "../../../../api/queries/useServiceBanners";
import { ServiceCategory, ServiceSubCategory } from "../../../../enum/service.enum";
import {
  DeleteSelectServiceBannerButtonIcon,
  SelectServiceBannerImageIcon,
} from "../../../../assets/icons/create-service-icons";

import SelectServiceBannerImageModal from "../../SelectServiceBannerImageModal";

const ServiceInformation = () => {
  const { t } = useTranslation("goService", { keyPrefix: "createServicePage.serviceInformationStep" });
  const { t: tCategories } = useTranslation("goService", { keyPrefix: "serviceCategory" });
  const { t: tSubCategories } = useTranslation("goService", { keyPrefix: "serviceSubCategory" });
  const { data: banners, isLoading: isLoadingBanners } = useServiceBanners();

  const {
    control,
    formState: { errors },
  } = useFormContext<CreateServiceFormData>();

  const [openSelectServiceBannerImageModal, setOpenSelectServiceBannerImageModal] = useState(false);
  const handleCloseSelectServiceBannerImageModal = () => setOpenSelectServiceBannerImageModal(false);
  const handleOpenSelectServiceBannerImageModal = () => setOpenSelectServiceBannerImageModal(true);

  return (
    <>
      <Box sx={styles.container}>
        {/* <Typography sx={styles.serviceCode}>{t("serviceCode", { code: "19823-AB" })}</Typography> */}
        <Controller
          name="name"
          control={control}
          render={({ field }) => (
            <FormControl fullWidth component="fieldset" error={!!errors.name}>
              <FormLabel error={!!errors.name}>{t("serviceName")}</FormLabel>
              <TextField {...field} fullWidth placeholder={t("enterServiceName")} error={!!errors.name} />
              {errors.name && <FormHelperText error>{t(errors.name.message as string)}</FormHelperText>}
            </FormControl>
          )}
        />
        <FormControl fullWidth component="fieldset" error={!!errors.banner}>
          <FormLabel sx={styles.formControlLabel}>{t("chooseServiceBannerImage")}</FormLabel>
          <Controller
            name="banner"
            control={control}
            render={({ field }) => (
              <>
                {field.value && banners?.find((b) => b.id === field.value)?.url && (
                  <>
                    <Box
                      component="img"
                      src={banners?.find((b) => b.id === field.value)?.url}
                      sx={styles.serviceBannerImage}
                    />
                    <IconButton onClick={() => field.onChange()} size="small" sx={styles.deleteServiceBannerButton}>
                      <DeleteSelectServiceBannerButtonIcon />
                    </IconButton>
                  </>
                )}
                {!field.value && (
                  <Box
                    sx={styles.serviceBannerContainer(!!errors.banner)}
                    onClick={handleOpenSelectServiceBannerImageModal}
                  >
                    <SelectServiceBannerImageIcon />
                    <Typography sx={styles.clickToSelect}>
                      {field.value ? t("changeBanner") : t("clickToSelect")}
                    </Typography>
                  </Box>
                )}
                {errors.banner && <FormHelperText error>{t(errors.banner.message as string)}</FormHelperText>}
              </>
            )}
          />
        </FormControl>
        <FormControl fullWidth error={!!errors.category}>
          <FormLabel error={!!errors.category}>{t("serviceCategory")}</FormLabel>
          <Controller
            name="category"
            control={control}
            render={({ field }) => (
              <Select {...field} displayEmpty fullWidth error={!!errors.category}>
                <MenuItem value="" disabled>
                  {t("selectOneCategory")}
                </MenuItem>
                {Object.values(ServiceCategory).map((category) => (
                  <MenuItem key={category} value={category}>
                    {tCategories(category)}
                  </MenuItem>
                ))}
              </Select>
            )}
          />
          {errors.category && <FormHelperText error>{t(errors.category.message as string)}</FormHelperText>}
        </FormControl>
        <FormControl fullWidth error={!!errors.subCategory}>
          <FormLabel>{t("serviceSubcategory")}</FormLabel>
          <Controller
            name="subCategory"
            control={control}
            render={({ field }) => (
              <Select {...field} displayEmpty fullWidth error={!!errors.subCategory}>
                <MenuItem value="" disabled>
                  {t("selectOneSubcategory")}
                </MenuItem>
                {Object.values(ServiceSubCategory).map((subCategory) => (
                  <MenuItem key={subCategory} value={subCategory}>
                    {tSubCategories(subCategory)}
                  </MenuItem>
                ))}
              </Select>
            )}
          />
          {errors.subCategory && <FormHelperText error>{t(errors.subCategory.message as string)}</FormHelperText>}
        </FormControl>
        <FormControl fullWidth component="fieldset" error={!!errors.description}>
          <FormLabel>{t("describeYourService")}</FormLabel>
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                placeholder={t("eGi")}
                multiline
                rows={5}
                inputProps={{ maxLength: 300 }}
                error={!!errors.description}
              />
            )}
          />
          {errors.description && <FormHelperText error>{t(errors.description.message as string)}</FormHelperText>}
        </FormControl>
      </Box>
      <SelectServiceBannerImageModal
        open={openSelectServiceBannerImageModal}
        closeModal={handleCloseSelectServiceBannerImageModal}
        banners={banners}
        isLoadingBanners={isLoadingBanners}
      />
    </>
  );
};

export default ServiceInformation;

const styles = {
  serviceBannerModalContainer: {
    width: "640px",
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    bgcolor: "#FFF",
    borderRadius: "12px",
    overflow: "auto",
    boxShadow:
      "0 20px 24px -4px var(--Colors-Effects-Shadows-shadow-xl_01, rgba(16, 24, 40, 0.08)), 0 8px 8px -4px var(--Colors-Effects-Shadows-shadow-xl_02, rgba(16, 24, 40, 0.03))",
    display: "flex",
    flexDirection: "column",
    gap: "24px",
  },
  serviceBannerModalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "24px 24px 0 24px",
  },
  serviceBannerModalHeaderTitle: {
    fontFamily: "Montserrat",
    color: "var(--colors-text-text-primary-900, #101828)",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "28px",
  },
  container: {
    width: { xs: "100%", md: "512px" },
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  serviceCode: {
    fontFamily: "Montserrat",
    color: "var(--Dark---600, #707070)",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
  },
  formControlLabel: {
    margin: 0,
    gap: "12px",
    fontFamily: "Montserrat",
    color: "var(--Dark---1000, #111)",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "24px",
  },
  radioImageGroup: {
    padding: "0 24px",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    position: "relative",
  },
  radioGroupRadio: { position: "absolute", top: 0, right: 0 },
  radioImage: {
    display: "block",
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  radioImageFormControlLabel: {
    position: "relative",
    width: "100%",
    height: "100px",
    margin: 0,
    border: "3px solid transparent",
    overflow: "hidden",
    borderRadius: "12px",
    transition: "all 0.2s ease-in-out",
    display: "flex",
    "& .MuiFormControlLabel-label": {
      flex: 1,
      height: "100%",
    },
    "&:has(.Mui-checked)": {
      border: "3px solid var(--Brand-600, #008B07)",
      zIndex: 1,
    },
    "&:hover": {
      borderColor: "var(--Gray-200, #E4E7EC)",
    },
  },
  serviceBannerContainer: (error: boolean) => ({
    padding: "16px 24px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    borderRadius: "12px",
    background: "var(--Base-White, #FFF)",
    cursor: "pointer",
    border: error
      ? "1px solid var(--Colors-Feedback-Error-error-500, #F24822)"
      : "1px solid var(--Gray---200, #CFCFCF)",
  }),
  clickToSelect: {
    color: "var(--Brand-700, #076D0D)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "20px",
  },
  serviceBannerModalFooter: {
    padding: "0 24px 24px",
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
  },
  serviceBannerImage: {
    width: "100%",
    height: "100px",
    objectFit: "cover",
  },
  deleteServiceBannerButton: {
    position: "absolute",
    top: "30px",
    right: 0,
  },
};
